import { IntlShape } from 'react-intl';

import { isDefined } from 'common/utils';
import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';

import { FilesMatch, MatchIdentifier } from './types';

const MIN_WIDTH = 1000;
const MAX_WIDTH = 10000;
const MIN_HEIGHT = 563;
const MAX_HEIGHT = 10000;
const ITEM_MAX_IMAGE_COUNT = 5;

export const getFileNameId = (fileName: string) => {
  // removes extension and index suffix from the file name
  return fileName.replace(/(\(\d\))?\.\w+$/g, '').trim();
};

export const readImageDimensions = (file: File): Promise<[number, number]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        resolve([image.width, image.height]);
      };
      image.onerror = () => {
        reject();
      };
      image.src = reader.result as string;
    };
    reader.onerror = () => {
      reject();
    };
    reader.readAsDataURL(file);
  });
};

export const buildOfferUpdatePayload = (offer: Offer, images: string[]) => {
  const sourceConfig = offer.source_config
    ? {
        ...offer.source_config,
        field_config: { ...offer.source_config.field_config, image_url: 'USER_INPUT' as const },
      }
    : null;

  const { scope, version, ...updatePayload } = {
    ...offer,
    image: images[0],
    images: images.map(url => ({ url, blurhash: null })),
    source_config: sourceConfig,
  };
  return updatePayload;
};

export const isImageFileName = (fileName: string) =>
  ['.jpeg', '.jpg', '.png'].includes(fileName.toLocaleLowerCase().substring(fileName.lastIndexOf('.')));

export const normalizeString = (string: string) => string.trim().replace(/(\s|\:|\/)+/g, '_');

const naturalFileNameSort = (fileA: File, fileB: File) =>
  fileA.name.length - fileB.name.length ||
  fileA.name.localeCompare(fileB.name, navigator.languages[0] || navigator.language, {
    numeric: true,
    ignorePunctuation: true,
  });

export const matchFilesWithOffers = (files: File[], items: Offer[], matchBy: MatchIdentifier, intl: IntlShape) => {
  const groupedSortedFiles = files.sort(naturalFileNameSort).reduce((group: Record<string, File[]>, file) => {
    const fileNameId = getFileNameId(file.name);

    if (!group[fileNameId]) {
      group[fileNameId] = [];
    }

    group[fileNameId].push(file);

    return group;
  }, {});

  return Promise.all(
    Object.values(groupedSortedFiles).map<Promise<FilesMatch>>(async files => {
      const hasAnyFileTypeError = files.some(file => !isImageFileName(file.name));
      const hasAnyZipFile = files.some(file => file.name.substring(file.name.lastIndexOf('.')) === '.zip');

      if (hasAnyFileTypeError) {
        return {
          status: 'failure',
          files,
          note: hasAnyZipFile
            ? intl.formatMessage({ id: 'menu.bulk-image.error-file-unzip' })
            : intl.formatMessage({ id: 'menu.bulk-image.error-file-type' }),
        };
      }

      try {
        const hasAnyDimensionError = (
          await Promise.all(
            files.map(async file => {
              const [width, height] = await readImageDimensions(file);
              return width < MIN_WIDTH || width > MAX_WIDTH || height < MIN_HEIGHT || height > MAX_HEIGHT;
            }),
          )
        ).includes(true);

        if (hasAnyDimensionError) {
          return {
            status: 'failure',
            files,
            note: intl.formatMessage({ id: 'menu.bulk-image.error-image-size' }),
          };
        }
      } catch (error) {
        return {
          status: 'failure',
          files,
          note: intl.formatMessage({ id: 'menu.bulk-image.error-file-reader' }),
        };
      }
      const matchedOffers = items.filter(item => {
        const fileNameId = getFileNameId(files[0].name);
        const matcher = item[matchBy];
        return matcher && normalizeString(matcher) === normalizeString(fileNameId);
      });
      if (matchedOffers.length === 0) {
        return {
          status: 'failure',
          files,
          note: intl.formatMessage({ id: 'menu.bulk-image.error-matching' }),
        };
      }
      if (files.length > ITEM_MAX_IMAGE_COUNT) {
        return {
          status: 'failure',
          files,
          matchedOffers,
          note: intl.formatMessage(
            { id: 'shared-menu.bulk-image-upload.error-max-image-count' },
            { count: files.length, maxImageCount: ITEM_MAX_IMAGE_COUNT },
          ),
        };
      }
      return {
        status: 'success',
        files,
        matchedOffers,
        note: [
          matchedOffers.length > 1 ? intl.formatMessage({ id: 'menu.bulk-image.multiple-matches' }) : undefined,
          matchedOffers.some(item => !!item.source_config)
            ? intl.formatMessage({ id: 'menu.bulk-image.feed-override' })
            : undefined,
        ]
          .filter(isDefined)
          .join('; '),
      };
    }),
  );
};
